// Components/Excel/ContactListExcelGenerator.js
import ExcelJS from "exceljs";
import dayjs from "dayjs";
import {
  countUniqueArFirms,
  countUniqueTradingStyleAdvisers,
  buildCountsByFirm,
  buildCountsForTradingStyles,
  getArFirmName,
  getPersonKey,
} from "../../HelperFunctions/GetFormatedData"; // adjust path to where your helper lives
import { CATS } from "../../HelperFunctions/GetAdviserCategories"; // if you already have this file


/* ---------------- utils ---------------- */
const clean = (v) => (v ?? "").toString().trim();

const getKey = (c) =>
  (c?.id || c?.email || c?.contactEmail || "").toString().trim().toLowerCase();

const uniqueCount = (records = []) => {
  const seen = new Set();
  for (const r of records) {
    const k = getKey(r);
    if (k) seen.add(k);
  }
  return seen.size;
};

const fillRowIfHasData = (row, argb) => {
  // treat as “data row” if it has anything in columns 1–9
  const hasAnyValue = row.values?.some((v, i) => i >= 1 && i <= 9 && clean(v));
  if (!hasAnyValue) return;

  row.eachCell({ includeEmpty: false }, (cell) => {
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb } };
  });
};

const normalizeStatuses = (raw) => {
  if (raw == null) return [];
  const parts = Array.isArray(raw) ? raw : [raw];
  return parts
    .flatMap((x) =>
      String(x)
        .split(/[,;|]/)
        .map((y) => y.trim())
    )
    .filter(Boolean)
    .map((s) => s.toUpperCase());
};

const buildCounts = (records = []) => {
  const counts = Object.fromEntries(CATS.map((c) => [c, 0]));
  const seen = new Set();

  for (const c of records) {
    const key = getKey(c);
    if (!key || seen.has(key)) continue;
    seen.add(key);

    const statuses = normalizeStatuses(c.status ?? c.adviserStatus);
    const hasFP = statuses.includes("FP");
    const hasMA = statuses.includes("MA");
    const hasGI =
      statuses.includes("GI/PROT") ||
      statuses.includes("GI") ||
      statuses.includes("GI/PROTECTION");

    if (hasFP && hasMA) counts[CATS[2]]++;
    else if (hasFP) counts[CATS[0]]++;
    else if (hasMA) counts[CATS[1]]++;
    else if (hasGI) counts[CATS[3]]++;
    else counts[CATS[4]]++;
  }

  return counts;
};

/* ---------------- Contact list helpers ---------------- */
const joinStyles = (v) => {
  if (Array.isArray(v))
    return v.map(clean).filter(Boolean).join(" / ") || "n/a";
  const s = clean(v);
  return s ? s : "-";
};

const hasTradingStyles = (c) => {
  const arr =
    Array.isArray(c?.tradingStyles) && c.tradingStyles.some((s) => clean(s));
  const single = !!clean(c?.tradingStyle);
  return arr || single;
};

const sortByLastThenFirst = (a, b) => {
  const la = clean(a.lastName).toLowerCase();
  const lb = clean(b.lastName).toLowerCase();
  if (la < lb) return -1;
  if (la > lb) return 1;
  const fa = clean(a.firstName).toLowerCase();
  const fb = clean(b.firstName).toLowerCase();
  return fa.localeCompare(fb);
};

const normalizeStatus = (s) => {
  if (s == null) return "-";
  if (Array.isArray(s)) return s.filter(Boolean).join(", ");
  if (typeof s === "object")
    return s.label || s.name || s.status || s.value || "-";
  if (typeof s === "boolean") return s ? "Yes" : "No";
  return String(s).trim() || "-";
};

const formatUKPhone = (num = "") => {
  const cl = String(num).replace(/\s+/g, "");
  return cl.startsWith("44") ? "0" + cl.slice(2) : cl;
};

const getTradingName = (c) => {
  const isAR = c?.isAR === true;
  const hasTradingName = !!clean(c?.tradingName);

  const hasTradingStyle = Array.isArray(c?.tradingStyles)
    ? c.tradingStyles.some((s) => !!clean(s))
    : !!clean(c?.tradingStyle);

  if (isAR && !hasTradingName && hasTradingStyle) {
    const first = clean(c?.firstName);
    const last = clean(c?.lastName);
    const full = [first, last].filter(Boolean).join(" ").trim();
    if (full) return full;
  }

  return clean(c?.tradingName || "") || "Unknown";
};



/* ---------------- Excel styles ---------------- */
const border = (top = "thin", bottom = "thin") => ({
  top: { style: "thin" },
  left: { style: "thin" },
  bottom: { style: "thin" },
  right: { style: "thin" },
});

const setRowBorders = (row, { top = "thin", bottom = "thin" } = {}) => {
  row.eachCell((cell) => {
    cell.border = border(top, bottom);
  });
};

const fillRow = (row, argb) => {
  row.eachCell((cell) => {
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb } };
  });
};

const setHeaderRowStyle = (row) => {
  row.font = { bold: true };
  row.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
  row.height = 18;

  row.eachCell((cell) => {
    cell.border = border("thin", "thin");
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFEFEFEF" },
    };
  });
};

/* ---------------- Columns ----------------
   IMPORTANT: do NOT use `header:` here, otherwise ExcelJS writes a header row at row 1 automatically.
*/
const setupColumns = (ws) => {
  ws.columns = [
    { key: "surname", width: 16 },
    { key: "forename", width: 16 },
    { key: "status", width: 10 },
    { key: "email", width: 34 },
    { key: "mobile", width: 16 },
    { key: "office", width: 16 },
    { key: "fca", width: 12 },
    { key: "address", width: 44 },
    { key: "styles", width: 28 },
  ];
};

const TABLE_HEADERS = [
  "SURNAME",
  "FORENAME",
  "STATUS",
  "EMAIL ADDRESS",
  "MOBILE NO.",
  "OFFICE NO.",
  "FCA NO.",
  "BUSINESS ADDRESS",
  "TRADING STYLES",
];

const addMainTableHeaderRow1 = (ws) => {
  ws.addRow(TABLE_HEADERS);
  const headerRow = ws.getRow(1);
  setHeaderRowStyle(headerRow);

  // Optional: filters like your screenshot
  ws.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: 9 },
  };
};

const addSectionHeading = (ws, title) => {
  ws.addRow([title]);
  const row = ws.lastRow;
  row.font = { bold: true };
  row.alignment = { vertical: "middle" };
  // Merge across all columns
  ws.mergeCells(row.number, 1, row.number, 9);
  // Keep it visually separated
  setRowBorders(row, { top: "medium", bottom: "medium" });
};

const groupRecords = (data = []) => {
  const groups = new Map();

  data.forEach((c) => {
    const key = getTradingName(c).toLowerCase();
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(c);
  });

  return Array.from(groups.entries())
    .map(([k, people]) => {
      const label = getTradingName(people[0]) || "Unknown";
      return [label, people.sort(sortByLastThenFirst)];
    })
    .sort(([a], [b]) => a.localeCompare(b));
};

// adds a visible “gap/break” between each new group
const addSpacerRow = (ws) => {
  ws.addRow([" "]);
  const r = ws.lastRow;
//   r.height = 6;
};

const addGroupedBlocks = (
  ws,
  data,
  { hideTradingStylesColumn = false, groupFill = "FFDDEBFF" } = {}
) => {
  const sortedGroups = groupRecords(data);

  sortedGroups.forEach(([groupName, people], groupIdx) => {
    // break before every new group (except the first)
    if (groupIdx > 0) addSpacerRow(ws);

    // Group title row (like your blue block row)
    ws.addRow([groupName]);
    const groupRow = ws.lastRow;

    groupRow.font = { bold: true };
    groupRow.alignment = { vertical: "middle" };

    // Merge title across SURNAME + FORENAME like your sheet
    ws.mergeCells(groupRow.number, 1, groupRow.number, 2);

    fillRow(groupRow, groupFill);
    setRowBorders(groupRow, { top: "medium", bottom: "thin" });

    // People rows
    people.forEach((c, idx) => {
      const email = clean(c.email || c.contactEmail || c.id || "-");
      const mobile =
        formatUKPhone(
          c.phone || c.mobile || c.mobileNumber || c.mobileTelephone || ""
        ) || "-";
      const office = formatUKPhone(c.office || c.officeNumber || "") || "-";
      const fca = clean(c.fcaNumber || c.fca || "-");
      const status = normalizeStatus(c.status ?? c.adviserStatus);
      const address = clean(c.businessAddress || "-");
      const styles = joinStyles(c.tradingStyles ?? c.tradingStyle);

      ws.addRow([
        c.lastName || "-",
        c.preferredName || c.firstName || "-",
        status,
        email,
        mobile,
        office,
        fca,
        address,
        hideTradingStylesColumn ? "" : styles,
      ]);

      const row = ws.lastRow;
      row.font = { size: 10 };
      row.alignment = { vertical: "top", wrapText: true };
      fillRowIfHasData(row, "FFF2F2F2");
      // Trading Styles section: merge Address + Styles (so styles column “disappears”)
      if (hideTradingStylesColumn) {
        ws.mergeCells(row.number, 8, row.number, 9);
      }

      // Right align phone + office + fca
      row.getCell(5).alignment = {
        ...row.getCell(5).alignment,
        horizontal: "right",
      };
      row.getCell(6).alignment = {
        ...row.getCell(6).alignment,
        horizontal: "right",
      };
      row.getCell(7).alignment = {
        ...row.getCell(7).alignment,
        horizontal: "right",
      };

      const isLast = idx === people.length - 1;
      setRowBorders(row, { top: "thin", bottom: isLast ? "medium" : "thin" });
    });
  });

  // gap after the whole section
  ws.addRow([]);
};

const buildSummarySheet = (workbook, contacts) => {
  const ws = workbook.addWorksheet("Summary");
  setupColumns(ws);

  // Title
  ws.addRow(["SUMMARY"]);
  const t = ws.lastRow;
  t.font = { bold: true, size: 18 };
  ws.mergeCells(t.number, 1, t.number, 9);

  ws.addRow([dayjs().format("DD MMM YYYY, hh:mm:ss A")]);
  ws.mergeCells(ws.lastRow.number, 1, ws.lastRow.number, 9);

  ws.addRow([]);

  const ars = (contacts || []).filter((d) => d.isAR === true);
  const trading = (contacts || []).filter((d) => d.isAR !== true);

  //  Total AR = unique firms (NOT advisers)
  const totalAR = countUniqueArFirms(ars);

  //  Total Trading Styles = unique advisers
  const totalTrading = countUniqueTradingStyleAdvisers(trading);

  //  AR counts = firm-based buckets
  const arCounts = buildCountsByFirm(ars, CATS);

  //  Trading counts = adviser-based buckets
  const tradingCounts = buildCountsForTradingStyles(trading, CATS);

  //  “IFA Advisers” table must be ALL INDIVIDUAL ADVISERS (AR + TS)
  // So combined counts must be adviser-based across ALL contacts (NOT arCounts + tradingCounts)
  const combinedCounts = buildCountsForTradingStyles(contacts, CATS);
  const totalCombined = Object.values(combinedCounts).reduce(
    (a, b) => a + b,
    0
  );

  // Header row
  ws.addRow(["IFA Advisers", "", "", "", "", "", "", "", "No."]);
  const hdr = ws.lastRow;
  hdr.font = { bold: true };
  ws.mergeCells(hdr.number, 1, hdr.number, 8);
  hdr.getCell(9).alignment = { horizontal: "right" };
  setRowBorders(hdr, { top: "thin", bottom: "thin" });
  fillRow(hdr, "FFEFEFEF");

  // Category rows
  for (const label of CATS) {
    ws.addRow([label, "", "", "", "", "", "", "", combinedCounts[label] || 0]);
    const r = ws.lastRow;
    ws.mergeCells(r.number, 1, r.number, 8);
    r.getCell(9).alignment = { horizontal: "right" };
    setRowBorders(r, { top: "thin", bottom: "thin" });
  }

  // Total row
  ws.addRow(["Total", "", "", "", "", "", "", "", totalCombined]);
  const totalRow = ws.lastRow;
  totalRow.font = { bold: true };
  ws.mergeCells(totalRow.number, 1, totalRow.number, 8);
  totalRow.getCell(9).alignment = { horizontal: "right" };
  setRowBorders(totalRow, { top: "thin", bottom: "medium" });

  ws.addRow([]);
  ws.addRow([`Total Appointed Representatives: ${totalAR}`]);
  ws.addRow([`Total Advisers in Trading Styles: ${totalTrading}`]);

  ws.views = [{ state: "frozen", ySplit: 4 }];
};

/* ---------------- Main export ---------------- */
export async function exportContactListToExcel({
  contacts = [],
  generatedBy = "ContactList Page",
  includeSummary = true,
}) {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = generatedBy;
  workbook.created = new Date();

  // Tab 1: Contact List
  const ws = workbook.addWorksheet("Contact List");
  setupColumns(ws);

  // start directly at row 1 with headers (no extra title rows)
  addMainTableHeaderRow1(ws);

  // freeze row 1 (headers stay unscrollable)
  ws.views = [{ state: "frozen", ySplit: 1 }];

  const ars = contacts.filter((c) => c.isAR === true);
  const nonArs = contacts.filter((c) => c.isAR !== true);

  // Section: Appointed Reps
  addSectionHeading(ws, "APPOINTED REPRESENTATIVES");
  addGroupedBlocks(ws, ars, {
    // mode: "AR",
    hideTradingStylesColumn: false,
    groupFill: "FFDDEBFF",
  });

  // Section: Trading Styles
  addSectionHeading(ws, "TRADING STYLES");
  addGroupedBlocks(ws, nonArs, {
    // mode: "TS",
    hideTradingStylesColumn: true,
    groupFill: "FFDDEBFF",
  });

  // Tab 2: Summary
    if (includeSummary) {
      buildSummarySheet(workbook, contacts);
    }
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const filename = `Contact List - ${dayjs().format("DD-MM-YYYY")}.xlsx`;
  return { blob, filename };
}
